import React, { useState, useMemo } from 'react';
import { calculate, findPriceForMargin } from '../lib/calculations';

const relTime = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60000);
  const hrs = Math.round(ms / 3600000);
  const days = Math.round(ms / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function MyProductsPanel({
  products,            // array of saved products
  currentResult,
  currentInputs,
  currentSettings,
  onSave,              // (name) => void
  onLoad,              // (product) => void
  onDelete,            // (id) => void
  onLoadExamples,      // () => void — populate with sample products
}) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState('list');         // 'list' | 'compare'
  const [sortMetric, setSortMetric] = useState('profit');  // 'profit' | 'margin' | 'revenue' | 'hourly' | 'etsyfees'
  const [diffMode, setDiffMode] = useState(false);
  // What-if simulator: target margin floor (15–50, default 30)
  const [marginFloor, setMarginFloor] = useState(30);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 3) next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed || !currentResult) return;
    onSave(trimmed);
    setName('');
  };

  // Compute fresh `live` calc results once for ALL saved products. All the
  // analytics below (aggregate KPIs, health score, tiers, margin-lift sim)
  // read from this single derived list so we don't re-run calculate() three
  // times on the same data.
  const liveItems = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products
      .map(p => {
        try { return { ...p, live: calculate(p.inputs, p.settings) }; }
        catch { return null; }
      })
      .filter(p => p && p.live && Number.isFinite(p.live.profit));
  }, [products]);

  // Aggregate KPIs — drives the headline dashboard cards.
  const aggregateStats = useMemo(() => {
    const items = liveItems;
    if (items.length === 0) return null;

    const totalProducts = items.length;
    const totalPotentialRevenue = items.reduce((s, p) => s + p.live.revenue, 0);
    const totalPotentialProfit = items.reduce((s, p) => s + p.live.profit, 0);
    const totalPotentialFees = items.reduce((s, p) => s + p.live.totalEtsyFees, 0);
    const avgMargin = items.reduce((s, p) => s + p.live.margin, 0) / totalProducts;
    const avgPrice = items.reduce((s, p) => s + (parseFloat(p.inputs?.price) || 0), 0) / totalProducts;

    // Top earner — highest profit per item
    let topEarner = items[0];
    let lowestMarginItem = items[0];
    items.forEach(p => {
      if (p.live.profit > topEarner.live.profit) topEarner = p;
      if (p.live.margin < lowestMarginItem.live.margin) lowestMarginItem = p;
    });

    // New this month
    const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const newThisMonth = items.filter(p => {
      const t = p.savedAt ? new Date(p.savedAt).getTime() : 0;
      return t > oneMonthAgo;
    }).length;

    // Currency from first product
    const sym = items[0].live.currencySymbol || '$';

    return {
      sym,
      totalProducts,
      newThisMonth,
      avgMargin,
      avgPrice,
      avgOrderValue: avgPrice, // alias for clarity in UI
      totalPotentialRevenue,
      totalPotentialProfit,
      totalPotentialFees,
      topEarner: { name: topEarner.name, profit: topEarner.live.profit, id: topEarner.id },
      lowestMarginItem: { name: lowestMarginItem.name, margin: lowestMarginItem.live.margin, id: lowestMarginItem.id },
    };
  }, [liveItems]);

  // Portfolio Health Score (0–100 with letter grade) — composite of margin,
  // profitability, and hourly-rate health. See helper for weighting.
  const portfolioHealth = useMemo(() => calcPortfolioHealth(liveItems), [liveItems]);

  // Performance tiers — auto-bucket each product into Stars / Solid / Risky / Losing.
  const tiers = useMemo(() => categorizeTiers(liveItems), [liveItems]);

  // What-if margin-floor simulator. Recomputes when slider or catalog changes.
  const marginLift = useMemo(
    () => computeMarginFloorLift(liveItems, marginFloor),
    [liveItems, marginFloor]
  );

  // Compute fresh results for selected products (so they reflect current rates)
  const selectedProducts = useMemo(() => {
    return products
      .filter(p => selected.has(p.id))
      .map(p => ({
        ...p,
        live: calculate(p.inputs, p.settings),
      }));
  }, [products, selected]);

  // ─── Compare view: visual cards + diff mode + sortable ─────────────
  if (view === 'compare' && selectedProducts.length >= 2) {
    const sym = selectedProducts[0].live.currencySymbol || '$';

    // Sort metric controls how cards are ordered + which winner is starred
    const metricOrder = sortMetric === 'profit'   ? selectedProducts.slice().sort((a,b) => b.live.profit - a.live.profit)
                      : sortMetric === 'margin'   ? selectedProducts.slice().sort((a,b) => b.live.margin - a.live.margin)
                      : sortMetric === 'revenue'  ? selectedProducts.slice().sort((a,b) => b.live.revenue - a.live.revenue)
                      : sortMetric === 'hourly'   ? selectedProducts.slice().sort((a,b) => (b.live.profitPerHour ?? -Infinity) - (a.live.profitPerHour ?? -Infinity))
                      : sortMetric === 'etsyfees' ? selectedProducts.slice().sort((a,b) => a.live.totalEtsyFees - b.live.totalEtsyFees)
                      : selectedProducts;

    // Baseline for diff mode = the first card after sort
    const baseline = diffMode ? metricOrder[0] : null;

    // Largest absolute value across all selected — for proportional bar widths
    const maxRevenue = Math.max(...selectedProducts.map(p => p.live.revenue));

    return (
      <div className="sketch-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <button
            onClick={() => { setView('list'); setDiffMode(false); }}
            className="font-body text-sm text-ink/82 hover:text-primary"
            style={{ fontWeight: 600 }}
          >
            ← Back to list
          </button>
          <h2 className="font-display text-2xl text-ink">
            🔬 Comparing {selectedProducts.length} products
          </h2>
        </div>

        {/* Toolbar — sort metric + diff mode toggle */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-body text-xs text-ink/78" style={{ fontWeight: 600 }}>Rank by:</span>
            <select
              value={sortMetric}
              onChange={(e) => setSortMetric(e.target.value)}
              style={{
                padding: '0.25rem 1.6rem 0.25rem 0.55rem',
                background: '#fefdf8',
                border: '1.5px solid #1a1a2e',
                borderRadius: '0.4rem',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <option value="profit">Net profit</option>
              <option value="margin">Margin</option>
              <option value="revenue">Revenue</option>
              <option value="hourly">Hourly rate</option>
              <option value="etsyfees">Lowest Etsy fees</option>
            </select>
          </div>
          {selectedProducts.length >= 2 && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.88rem',
                fontWeight: 600,
                color: '#1a1a2e',
              }}
            >
              <input
                type="checkbox"
                checked={diffMode}
                onChange={(e) => setDiffMode(e.target.checked)}
                style={{ accentColor: '#1e3a8a', width: '15px', height: '15px' }}
              />
              Show diffs vs winner
            </label>
          )}
        </div>

        {/* Visual cards — one per product, side by side */}
        <div
          className="compare-cards-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: selectedProducts.length === 2
              ? 'repeat(2, minmax(0, 1fr))'
              : 'repeat(3, minmax(0, 1fr))',
            gap: '0.75rem',
          }}
        >
          {metricOrder.map((p, idx) => {
            const isWinner = idx === 0;
            const live = p.live;
            const profitColor = live.profit >= 0 ? '#047857' : '#9f1239';
            const marginColor = live.margin >= 30 ? '#047857' : live.margin >= 15 ? '#b45309' : '#9f1239';
            const hourlyColor = live.profitPerHour == null ? '#1a1a2e'
                              : live.profitPerHour >= 25 ? '#047857'
                              : live.profitPerHour >= 12 ? '#b45309' : '#9f1239';

            const diff = (cur, base) => {
              if (!diffMode || isWinner || !Number.isFinite(cur) || !Number.isFinite(base)) return null;
              const d = cur - base;
              if (Math.abs(d) < 0.005) return null;
              return d;
            };

            const renderDiff = (d, suffix = '', color) => {
              if (d == null) return null;
              const sign = d > 0 ? '+' : '';
              return (
                <span
                  className="font-body text-xs ml-1"
                  style={{
                    color: color || (d > 0 ? '#047857' : '#9f1239'),
                    fontWeight: 600,
                  }}
                >
                  ({sign}{d.toFixed(suffix === '%' ? 1 : 2)}{suffix})
                </span>
              );
            };

            return (
              <div
                key={p.id}
                style={{
                  background: '#fefdf8',
                  border: isWinner ? '2.5px solid #047857' : '1.5px solid rgba(26,26,46,0.12)',
                  borderRadius: '0.75rem',
                  padding: '0.85rem',
                  boxShadow: isWinner ? '0 1px 2px rgba(4,120,87,0.1), 0 4px 12px rgba(4,120,87,0.08)' : '0 1px 2px rgba(26,26,46,0.04)',
                  position: 'relative',
                  minWidth: 0,
                }}
              >
                {isWinner && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '0.85rem',
                      background: '#047857',
                      color: '#fefdf8',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '0.3rem',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    ★ {sortMetric === 'profit' ? 'Most profit' : sortMetric === 'margin' ? 'Best margin' : sortMetric === 'revenue' ? 'Most revenue' : sortMetric === 'hourly' ? 'Best hourly' : 'Lowest fees'}
                  </div>
                )}

                {/* Product name */}
                <h3
                  className="font-display text-base text-ink mb-2 truncate"
                  style={{ fontWeight: 600 }}
                  title={p.name}
                >
                  {p.name}
                </h3>

                {/* Net Profit — the headline */}
                <div className="mb-3">
                  <p className="font-body text-xs text-ink/72 mb-0.5" style={{ fontWeight: 600 }}>
                    Net profit
                  </p>
                  <div className="flex items-baseline flex-wrap">
                    <span
                      className="font-numbers"
                      style={{
                        fontSize: '1.65rem',
                        fontWeight: 700,
                        color: profitColor,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {sym}{live.profit.toFixed(2)}
                    </span>
                    {baseline && renderDiff(diff(live.profit, baseline.live.profit), '')}
                  </div>
                </div>

                {/* Inline mini-bars — Revenue with proportional width */}
                <div className="mb-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-body text-xs text-ink/78">Revenue</span>
                    <span
                      className="font-numbers text-xs"
                      style={{ fontWeight: 600, color: '#047857', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {sym}{live.revenue.toFixed(2)}
                      {baseline && renderDiff(diff(live.revenue, baseline.live.revenue), '')}
                    </span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '6px',
                      background: '#ffffff',
                      border: '1.5px solid rgba(26,26,46,0.18)',
                      borderRadius: '999px',
                      overflow: 'hidden',
                      marginTop: '3px',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min((live.revenue / maxRevenue) * 100, 100)}%`,
                        height: '100%',
                        background: '#34d399',
                      }}
                    />
                  </div>
                </div>

                {/* Cost bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-body text-xs text-ink/78">Total cost</span>
                    <span
                      className="font-numbers text-xs"
                      style={{ fontWeight: 600, color: '#c2410c', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {sym}{live.totalCost.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '6px',
                      background: '#ffffff',
                      border: '1.5px solid rgba(26,26,46,0.18)',
                      borderRadius: '999px',
                      overflow: 'hidden',
                      marginTop: '3px',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min((live.totalCost / maxRevenue) * 100, 100)}%`,
                        height: '100%',
                        background: '#fb923c',
                      }}
                    />
                  </div>
                </div>

                {/* Stats grid — Margin, Hourly, Etsy Takes, Markup */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.4rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px dashed rgba(26,26,46,0.2)',
                  }}
                >
                  <div>
                    <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>Margin</p>
                    <p
                      className="font-numbers text-base"
                      style={{ fontWeight: 700, color: marginColor, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {live.margin}%
                      {baseline && renderDiff(diff(live.margin, baseline.live.margin), 'pp')}
                    </p>
                  </div>
                  {live.profitPerHour !== null && Number.isFinite(live.profitPerHour) ? (
                    <div title={`${live.totalLaborHours}h of work`}>
                      <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>Per hour</p>
                      <p
                        className="font-numbers text-base"
                        style={{ fontWeight: 700, color: hourlyColor, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {sym}{live.profitPerHour}
                        {baseline && live.profitPerHour != null && baseline.live.profitPerHour != null &&
                          renderDiff(diff(live.profitPerHour, baseline.live.profitPerHour), '')}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>Etsy takes</p>
                      <p
                        className="font-numbers text-base text-ink"
                        style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {live.effectiveFeeRate}%
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>Markup</p>
                    <p className="font-numbers text-base text-ink" style={{ fontWeight: 700 }}>
                      {live.markup}×
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>Etsy fees</p>
                    <p
                      className="font-numbers text-base text-ink"
                      style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {sym}{live.totalEtsyFees.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Price + qty footer */}
                <p className="font-body text-xs text-ink/78 mt-2 pt-2" style={{ borderTop: '1px dashed rgba(26,26,46,0.1)' }}>
                  {sym}{p.inputs?.price || '—'} × {p.inputs?.quantity || 1}
                </p>
              </div>
            );
          })}
        </div>

        <p className="font-note text-xs text-ink/68 mt-4">
          ~ live exchange rates · {diffMode ? 'showing diffs vs winner' : `ranked by ${sortMetric === 'profit' ? 'net profit' : sortMetric === 'margin' ? 'margin' : sortMetric === 'revenue' ? 'revenue' : sortMetric === 'hourly' ? 'hourly rate' : 'lowest fees'}`} ~
        </p>
      </div>
    );
  }

    // ─── List view (default) ────────────────────────────────
  return (
    <div className="sketch-card">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h2 className="font-display text-2xl text-ink">📊 Portfolio Analytics</h2>
        {products.length >= 2 && (() => {
          const ready = selected.size >= 2;
          const label = ready
            ? `Compare ${selected.size} →`
            : selected.size === 1
              ? 'Pick 1 more to compare'
              : 'Select 2 to compare';
          return (
            <button
              onClick={() => ready && setView('compare')}
              disabled={!ready}
              title={ready ? 'Compare the selected products side by side' : 'Tick at least 2 products in the list below'}
              style={{
                padding: '0.45rem 0.9rem',
                backgroundColor: ready ? '#1e3a8a' : '#fefdf8',
                color: ready ? '#fefefe' : 'rgba(26, 26, 46, 0.82)',
                border: ready ? '2px solid #1a1a2e' : '2px dashed rgba(26, 26, 46, 0.3)',
                borderRadius: '0.625rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.93rem',
                boxShadow: ready ? '2px 3px 0 rgba(26, 26, 46, 0.2)' : 'none',
                cursor: ready ? 'pointer' : 'not-allowed',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              🔬 {label}
            </button>
          );
        })()}
      </div>
      <p className="font-body text-sm text-ink/78 mb-4">
        Track, compare, and stress-test your catalog. Save 2+ to unlock side-by-side
        comparison and what-if simulations.
      </p>

      {/* Hero analytics band — Health Score (left) + Performance Tiers (right) */}
      {portfolioHealth && tiers && (
        <div
          className="mb-4"
          style={{
            display: 'grid',
            // Stacks to 1 column at narrow widths so neither card gets squeezed
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.6rem',
          }}
        >
          <HealthScoreCard health={portfolioHealth} />
          <PerformanceTiersCard tiers={tiers} sym={aggregateStats?.sym || '$'} />
        </div>
      )}

      {/* Dashboard — aggregate KPIs across all saved products */}
      {aggregateStats && (
        <div
          className="mb-4"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '0.5rem',
          }}
        >
          {/* Card 1: Total products */}
          <KpiCard
            label="PRODUCTS SAVED"
            value={aggregateStats.totalProducts}
            sublabel={
              aggregateStats.newThisMonth > 0
                ? `+${aggregateStats.newThisMonth} this month`
                : 'no new this month'
            }
            accent="#1e3a8a"
            accentBg="rgba(30, 58, 138, 0.05)"
            icon="📦"
          />
          {/* Card 2: Average margin */}
          <KpiCard
            label="AVG MARGIN"
            value={`${aggregateStats.avgMargin.toFixed(1)}%`}
            sublabel={
              aggregateStats.avgMargin >= 30 ? 'healthy across the board'
              : aggregateStats.avgMargin >= 15 ? 'okay — could be higher'
              : 'tight — many products underpriced'
            }
            valueColor={
              aggregateStats.avgMargin >= 30 ? '#047857'
              : aggregateStats.avgMargin >= 15 ? '#b45309'
              : '#9f1239'
            }
            accent={
              aggregateStats.avgMargin >= 30 ? '#047857'
              : aggregateStats.avgMargin >= 15 ? '#b45309'
              : '#9f1239'
            }
            accentBg={
              aggregateStats.avgMargin >= 30 ? 'rgba(4, 120, 87, 0.05)'
              : aggregateStats.avgMargin >= 15 ? 'rgba(180, 83, 9, 0.05)'
              : 'rgba(159, 18, 57, 0.05)'
            }
            icon="📐"
          />
          {/* Card 3: Top earner — full width on the second row */}
          <KpiCard
            label="TOP EARNER"
            value={`${aggregateStats.sym}${aggregateStats.topEarner.profit.toFixed(2)}`}
            sublabel={aggregateStats.topEarner.name}
            valueColor="#047857"
            accent="#047857"
            accentBg="rgba(4, 120, 87, 0.05)"
            icon="🏆"
          />
          {/* Card 4: Portfolio AOV */}
          <KpiCard
            label="AVG ORDER VALUE"
            value={`${aggregateStats.sym}${aggregateStats.avgOrderValue.toFixed(2)}`}
            sublabel={`profit per item: ${aggregateStats.sym}${(aggregateStats.totalPotentialProfit / aggregateStats.totalProducts).toFixed(2)}`}
            accent="#7e22ce"
            accentBg="rgba(126, 34, 206, 0.05)"
            icon="💰"
          />
        </div>
      )}

      {/* Insight callout — one-line takeaway under the cards */}
      {aggregateStats && aggregateStats.totalProducts >= 3 && (
        <div
          className="mb-4"
          style={{
            padding: '0.7rem 0.9rem',
            background: 'rgba(180, 83, 9, 0.06)',
            border: '1px dashed rgba(180, 83, 9, 0.35)',
            borderRadius: '0.5rem',
            fontSize: '0.88rem',
            color: '#1a1a2e',
            fontFamily: 'Inter, sans-serif',
            lineHeight: 1.5,
          }}
        >
          <span style={{ marginRight: '0.4rem' }}>💡</span>
          {aggregateStats.lowestMarginItem.margin < 15 ? (
            <>
              <strong>{aggregateStats.lowestMarginItem.name}</strong> has the lowest margin
              ({aggregateStats.lowestMarginItem.margin.toFixed(1)}%). Reload it and consider re-pricing.
            </>
          ) : aggregateStats.avgMargin >= 30 ? (
            <>
              All {aggregateStats.totalProducts} products average{' '}
              <strong>{aggregateStats.avgMargin.toFixed(1)}% margin</strong> — selling 30 of each per month would net{' '}
              <strong>{aggregateStats.sym}{(aggregateStats.totalPotentialProfit * 30 * 12).toLocaleString('en-US', { maximumFractionDigits: 0 })}/year</strong>.
            </>
          ) : (
            <>
              Average margin is <strong>{aggregateStats.avgMargin.toFixed(1)}%</strong>.
              Try re-pricing your weaker products toward 30% — small price bumps compound.
            </>
          )}
        </div>
      )}

      {/* What-if margin floor simulator — slider + projected impact */}
      {marginLift && liveItems.length >= 2 && (
        <MarginFloorSimulator
          marginFloor={marginFloor}
          setMarginFloor={setMarginFloor}
          lift={marginLift}
          totalProducts={liveItems.length}
          sym={aggregateStats?.sym || '$'}
        />
      )}

      {/* Save current */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          placeholder={currentResult ? 'Name this product…' : 'Calculate something first to save'}
          className="sketch-input flex-1 mt-0"
          style={{ minWidth: '200px' }}
          maxLength={50}
          disabled={!currentResult}
        />
        <button
          onClick={handleSave}
          disabled={!currentResult || !name.trim()}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: currentResult && name.trim() ? '#1e3a8a' : '#fefefe',
            color: currentResult && name.trim() ? '#fefefe' : 'rgba(26,26,46,0.4)',
            border: '2px solid #1a1a2e',
            borderRadius: '0.625rem',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: '0.9rem',
            boxShadow: currentResult && name.trim() ? '2px 3px 0 rgba(26,26,46,0.2)' : 'none',
            cursor: currentResult && name.trim() ? 'pointer' : 'not-allowed',
            opacity: currentResult && name.trim() ? 1 : 0.6,
            whiteSpace: 'nowrap',
          }}
        >
          + Save
        </button>
      </div>

      {products.length === 0 ? (
        <div
          className="px-5 py-6"
          style={{
            border: '1.5px dashed rgba(26,26,46,0.18)',
            borderRadius: '0.75rem',
            background: 'linear-gradient(180deg, rgba(30,58,138,0.03) 0%, rgba(180,83,9,0.03) 100%)',
          }}
        >
          <div className="text-center mb-5">
            <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }} aria-hidden="true">📊</div>
            <p className="font-display text-xl text-ink" style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
              Build your portfolio, then unlock analytics
            </p>
            <p className="font-body text-sm text-ink/78" style={{ lineHeight: 1.5, maxWidth: '34rem', margin: '0 auto' }}>
              Calculate any product, then name and save it here. The more you save,
              the more the portfolio dashboard reveals.
            </p>
          </div>

          {/* Feature preview tiles — what gets unlocked */}
          <div
            className="empty-feature-grid"
            style={{
              display: 'grid',
              // Wraps to 1–2 cols on narrow viewports
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.55rem',
              marginBottom: '1.1rem',
            }}
          >
            <FeaturePreviewTile
              icon="🩺"
              title="Health Score"
              desc="Composite 0–100 grade with margin, profitability + hourly checks."
              accent="#1e3a8a"
            />
            <FeaturePreviewTile
              icon="🎯"
              title="Performance Tiers"
              desc="Auto-bucket each product into Stars, Solid, Risky, or Losing."
              accent="#047857"
            />
            <FeaturePreviewTile
              icon="🧪"
              title="What-if Simulator"
              desc="Lift everyone to a margin floor and see the catalog-wide impact."
              accent="#b45309"
            />
          </div>

          {onLoadExamples && (
            <div className="text-center">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  margin: '0 auto 0.85rem',
                  maxWidth: '14rem',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.86rem',
                  color: 'rgba(26,26,46,0.4)',
                  letterSpacing: '0.06em',
                }}
              >
                <span style={{ flex: 1, height: '1px', background: 'rgba(26,26,46,0.15)' }} />
                TRY IT NOW
                <span style={{ flex: 1, height: '1px', background: 'rgba(26,26,46,0.15)' }} />
              </div>
              <button
                onClick={onLoadExamples}
                style={{
                  padding: '0.55rem 1.1rem',
                  background: '#1a1a2e',
                  border: '1.5px solid #1a1a2e',
                  borderRadius: '0.55rem',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  color: '#fefdf8',
                  cursor: 'pointer',
                  boxShadow: '2px 3px 0 rgba(26,26,46,0.18)',
                  transition: 'all 0.12s',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#1a1a2e'; }}
              >
                ✨ Load 3 example products
              </button>
              <p className="font-body text-xs text-ink/72 mt-2" style={{ fontSize: '0.82rem' }}>
                Instantly populates the dashboard. Delete anytime.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {products.length === 1 && (
            <p className="font-body text-xs text-ink/72 mb-2">
              ✨ One product saved. Save another to unlock side-by-side comparison.
            </p>
          )}
          {products.length >= 2 && (
            <p className="font-body text-xs text-ink/72 mb-2 flex items-center gap-1.5">
              <span aria-hidden="true">☐</span>
              {selected.size === 0 && (
                <>tick 2–3 below to compare side by side</>
              )}
              {selected.size === 1 && (
                <>{selected.size} selected · pick 1 more to enable Compare</>
              )}
              {selected.size >= 2 && (
                <>{selected.size} of {Math.min(products.length, 3)} selected · click <strong>Compare</strong> above</>
              )}
            </p>
          )}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {products.map(p => {
              const isSelected = selected.has(p.id);
              const canSelect = selected.size < 3 || isSelected;
              return (
                <li
                  key={p.id}
                  style={{
                    border: isSelected ? '2px solid #1e3a8a' : '1.5px solid rgba(26,26,46,0.12)',
                    borderRadius: '0.625rem',
                    background: isSelected ? 'rgba(30, 58, 138, 0.04)' : '#fefdf8',
                    padding: '0.75rem 0.85rem',
                    transition: 'all 0.15s',
                  }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!canSelect}
                      onChange={() => toggleSelect(p.id)}
                      style={{
                        marginTop: '4px',
                        width: '18px',
                        height: '18px',
                        cursor: canSelect ? 'pointer' : 'not-allowed',
                        flexShrink: 0,
                        accentColor: '#1e3a8a',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="font-body text-base text-ink truncate" style={{ fontWeight: 600 }}>
                        {p.name}
                      </p>
                      <p className="font-body text-xs text-ink/78 mt-0.5">
                        <span className="font-numbers" style={{ fontWeight: 600 }}>${p.price}</span>
                        {' · profit '}
                        <span className="font-numbers" style={{
                          fontWeight: 600,
                          color: p.profit >= 0 ? '#047857' : '#9f1239'
                        }}>
                          ${p.profit?.toFixed(2)}
                        </span>
                        {' · '}
                        <span className="font-numbers">{p.margin}%</span>
                        {' margin · '}
                        <span className="text-ink/78">{relTime(p.savedAt)}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => onLoad(p)}
                        title="Load this product into the calculator"
                        style={{
                          padding: '0.25rem 0.6rem',
                          background: '#fefdf8',
                          border: '1.5px solid #1a1a2e',
                          borderRadius: '0.4rem',
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#1a1a2e',
                          cursor: 'pointer',
                          boxShadow: '1px 2px 0 rgba(26,26,46,0.15)',
                        }}
                      >
                        Load
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete "${p.name}"?`)) onDelete(p.id);
                        }}
                        title="Delete"
                        aria-label={`Delete ${p.name}`}
                        style={{
                          padding: '0.25rem 0.45rem',
                          background: 'transparent',
                          border: 'none',
                          color: 'rgba(26,26,46,0.4)',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          lineHeight: 1,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#9f1239'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(26,26,46,0.4)'}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="font-body text-xs text-ink/78 mt-3">
            Up to 20 products saved locally. Pick 2–3 to compare.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Helper: Portfolio Health Score (0–100, with letter grade) ───────
//
// Composite of four sub-scores:
//   • Avg margin       (0–40 pts): linear from 0% (=0) to 40%+ (=40)
//   • % profitable     (0–30 pts): share of catalog with profit > 0
//   • % healthy margin (0–20 pts): share with margin ≥ 25%
//   • % healthy hourly (0–10 pts): of products with labor data, share at ≥$20/hr
// If no product has labor data, the 10pt slot is set aside and the other 90 pts
// scale up to 100, so the score isn't artificially capped.
function calcPortfolioHealth(items) {
  if (!items || items.length === 0) return null;
  const n = items.length;

  const avgMargin = items.reduce((s, p) => s + p.live.margin, 0) / n;
  const marginScore = Math.max(0, Math.min(40, (avgMargin / 40) * 40));

  const profitable = items.filter(p => p.live.profit > 0).length;
  const profitableScore = (profitable / n) * 30;

  const healthyMargin = items.filter(p => p.live.margin >= 25).length;
  const healthyMarginScore = (healthyMargin / n) * 20;

  const withLabor = items.filter(p => Number.isFinite(p.live.profitPerHour));
  let hourlyScore = 0;
  let hourlyApplicable = false;
  let healthyHourly = 0;
  if (withLabor.length > 0) {
    hourlyApplicable = true;
    healthyHourly = withLabor.filter(p => p.live.profitPerHour >= 20).length;
    hourlyScore = (healthyHourly / withLabor.length) * 10;
  }

  let total = marginScore + profitableScore + healthyMarginScore + hourlyScore;
  if (!hourlyApplicable) total = total * (100 / 90); // rebalance 90→100
  const score = Math.max(0, Math.min(100, Math.round(total)));

  const grade =
    score >= 90 ? 'A' :
    score >= 80 ? 'B' :
    score >= 70 ? 'C' :
    score >= 60 ? 'D' : 'F';

  const color =
    score >= 80 ? '#047857' :
    score >= 65 ? '#65a30d' :
    score >= 50 ? '#b45309' : '#9f1239';

  return {
    score, grade, color,
    breakdown: [
      { label: 'Avg margin',    score: Math.round(marginScore),        max: 40, value: `${avgMargin.toFixed(1)}%` },
      { label: 'Profitable',    score: Math.round(profitableScore),    max: 30, value: `${profitable}/${n}` },
      { label: 'Margin ≥25%',   score: Math.round(healthyMarginScore), max: 20, value: `${healthyMargin}/${n}` },
      hourlyApplicable
        ? { label: 'Hourly ≥$20', score: Math.round(hourlyScore), max: 10, value: `${healthyHourly}/${withLabor.length}` }
        : { label: 'Add labor', score: 0, max: 10, value: 'no data', muted: true },
    ],
  };
}

// ─── Helper: Performance Tier categorization ─────────────────────────
//
//   🌟 Stars         margin ≥ 30% AND profit > 0
//   💪 Solid         margin 15–30% AND profit > 0
//   ⚠️ Risky         margin < 15% AND profit > 0
//   🚨 Losing money  profit ≤ 0
function categorizeTiers(items) {
  if (!items || items.length === 0) return null;
  const stars = [], solid = [], risky = [], losing = [];
  for (const p of items) {
    if (p.live.profit <= 0) losing.push(p);
    else if (p.live.margin >= 30) stars.push(p);
    else if (p.live.margin >= 15) solid.push(p);
    else risky.push(p);
  }
  return { stars, solid, risky, losing };
}

// ─── Helper: What-if margin floor simulation ─────────────────────────
//
// For each product whose current margin is below targetMargin, ask the
// pricing engine for the price that would hit targetMargin and compute
// the per-item profit lift. Aggregates lift across the catalog and
// projects a yearly impact at 30 sales/product/month.
function computeMarginFloorLift(items, targetMargin) {
  if (!items || items.length === 0) return null;
  const affected = [];
  for (const p of items) {
    if (p.live.margin >= targetMargin) continue;
    const np = findPriceForMargin(p.inputs, p.settings, targetMargin);
    if (!np || !np.exact || np.exact <= 0) continue;
    const newInputs = { ...p.inputs, price: String(np.exact) };
    let newResult;
    try { newResult = calculate(newInputs, p.settings); }
    catch { continue; }
    if (!newResult || !Number.isFinite(newResult.profit)) continue;
    const lift = newResult.profit - p.live.profit;
    if (lift <= 0.005) continue;
    affected.push({
      id: p.id, name: p.name,
      oldPrice: parseFloat(p.inputs?.price) || 0,
      newPrice: np.exact,
      oldMargin: p.live.margin,
      newMargin: targetMargin,
      lift,
    });
  }
  const totalLift = affected.reduce((s, a) => s + a.lift, 0);
  return {
    affected,
    totalLift,
    monthlyAt30: totalLift * 30,
    yearly: totalLift * 30 * 12,
  };
}

// ─── Empty-state feature preview tile ────────────────────────────────
function FeaturePreviewTile({ icon, title, desc, accent }) {
  return (
    <div
      style={{
        background: '#fefdf8',
        border: `1.5px solid ${accent}33`,
        borderTop: `3px solid ${accent}`,
        borderRadius: '0.55rem',
        padding: '0.7rem 0.8rem',
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: '1.3rem', marginBottom: '0.25rem' }} aria-hidden="true">{icon}</div>
      <p
        className="font-body"
        style={{ fontSize: '0.86rem', fontWeight: 700, color: accent, marginBottom: '0.2rem', letterSpacing: '-0.01em' }}
      >
        {title}
      </p>
      <p
        className="font-body"
        style={{ fontSize: '0.78rem', color: 'rgba(26,26,46,0.72)', lineHeight: 1.4, margin: 0 }}
      >
        {desc}
      </p>
    </div>
  );
}

// ─── Health Score card ───────────────────────────────────────────────
function HealthScoreCard({ health }) {
  if (!health) return null;
  const { score, grade, color, breakdown } = health;

  return (
    <div
      style={{
        background: `linear-gradient(180deg, ${color}0d 0%, ${color}05 100%)`,
        border: `1.5px solid ${color}66`,
        borderRadius: '0.625rem',
        padding: '0.75rem 0.85rem',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
      title={`Composite of margin, profitability, and hourly-rate health across all saved products`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span style={{ fontSize: '1.02rem' }} aria-hidden="true">🩺</span>
        <span
          className="font-body"
          style={{ fontSize: '0.74rem', color, fontWeight: 700, letterSpacing: '0.08em' }}
        >
          PORTFOLIO HEALTH
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.55rem', marginBottom: '0.45rem' }}>
        <p
          className="font-numbers"
          style={{
            fontSize: '2.1rem', fontWeight: 700, color,
            letterSpacing: '-0.04em', lineHeight: 1, margin: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {score}
        </p>
        <p
          className="font-body"
          style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(26,26,46,0.6)', margin: 0 }}
        >
          / 100
        </p>
        <div
          style={{
            marginLeft: 'auto',
            background: color,
            color: '#fefdf8',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.95rem',
            fontWeight: 800,
            letterSpacing: '0.04em',
            padding: '0.18rem 0.55rem',
            borderRadius: '0.4rem',
            lineHeight: 1.1,
          }}
        >
          {grade}
        </div>
      </div>
      {/* Compact breakdown — micro-bars per sub-score */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.22rem' }}>
        {breakdown.map((b) => {
          const pct = b.max > 0 ? (b.score / b.max) * 100 : 0;
          const muted = b.muted;
          return (
            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
              <span
                className="font-body"
                style={{ fontSize: '0.72rem', color: muted ? 'rgba(26,26,46,0.4)' : 'rgba(26,26,46,0.72)', flexShrink: 0, minWidth: '5.2rem' }}
              >
                {b.label}
              </span>
              <div style={{ flex: 1, height: '5px', background: 'rgba(26,26,46,0.08)', borderRadius: '999px', overflow: 'hidden', minWidth: 0 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: muted ? 'rgba(26,26,46,0.18)' : color }} />
              </div>
              <span
                className="font-body"
                style={{ fontSize: '0.7rem', color: muted ? 'rgba(26,26,46,0.4)' : 'rgba(26,26,46,0.62)', flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: '2.8rem', textAlign: 'right' }}
              >
                {b.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Performance Tiers card ──────────────────────────────────────────
function PerformanceTiersCard({ tiers, sym }) {
  if (!tiers) return null;
  const { stars, solid, risky, losing } = tiers;
  const total = stars.length + solid.length + risky.length + losing.length;

  const TIERS = [
    { key: 'stars',  emoji: '🌟', label: 'Stars',     desc: 'margin ≥ 30%',     count: stars.length,  color: '#047857' },
    { key: 'solid',  emoji: '💪', label: 'Solid',     desc: 'margin 15–30%',    count: solid.length,  color: '#65a30d' },
    { key: 'risky',  emoji: '⚠️', label: 'Risky',     desc: 'margin < 15%',     count: risky.length,  color: '#b45309' },
    { key: 'losing', emoji: '🚨', label: 'Losing $',  desc: 'profit ≤ 0',       count: losing.length, color: '#9f1239' },
  ];

  return (
    <div
      style={{
        background: '#fefdf8',
        border: '1.5px solid rgba(26,26,46,0.18)',
        borderRadius: '0.625rem',
        padding: '0.75rem 0.85rem',
        minWidth: 0,
      }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ fontSize: '1.02rem' }} aria-hidden="true">🎯</span>
        <span
          className="font-body"
          style={{ fontSize: '0.74rem', color: '#1a1a2e', fontWeight: 700, letterSpacing: '0.08em' }}
        >
          PERFORMANCE TIERS
        </span>
        <span className="font-body" style={{ marginLeft: 'auto', fontSize: '0.74rem', color: 'rgba(26,26,46,0.55)' }}>
          {total} product{total === 1 ? '' : 's'}
        </span>
      </div>
      {/* Stacked proportion bar */}
      <div style={{ display: 'flex', height: '10px', borderRadius: '999px', overflow: 'hidden', border: '1px solid rgba(26,26,46,0.18)', marginBottom: '0.55rem' }}>
        {TIERS.map(t => t.count > 0 && (
          <div
            key={t.key}
            style={{ width: `${(t.count / total) * 100}%`, background: t.color }}
            title={`${t.label}: ${t.count}`}
          />
        ))}
      </div>
      {/* Tier chips — 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.35rem' }}>
        {TIERS.map(t => (
          <div
            key={t.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.3rem 0.45rem',
              background: t.count > 0 ? `${t.color}10` : 'transparent',
              border: `1px solid ${t.count > 0 ? `${t.color}55` : 'rgba(26,26,46,0.1)'}`,
              borderRadius: '0.4rem',
              opacity: t.count === 0 ? 0.55 : 1,
              minWidth: 0,
            }}
          >
            <span style={{ fontSize: '0.95rem', flexShrink: 0 }} aria-hidden="true">{t.emoji}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                className="font-body"
                style={{ fontSize: '0.78rem', fontWeight: 700, color: t.count > 0 ? t.color : 'rgba(26,26,46,0.6)', lineHeight: 1.1, margin: 0 }}
              >
                {t.count} {t.label}
              </p>
              <p
                className="font-body"
                style={{ fontSize: '0.66rem', color: 'rgba(26,26,46,0.55)', lineHeight: 1.1, margin: 0 }}
              >
                {t.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── What-if Margin Floor simulator ──────────────────────────────────
function MarginFloorSimulator({ marginFloor, setMarginFloor, lift, totalProducts, sym }) {
  const affected = lift?.affected?.length || 0;
  const fmt = (v) => `${sym}${Math.abs(v).toFixed(2)}`;
  const fmtBig = (v) => v >= 1000
    ? `${sym}${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : `${sym}${v.toFixed(0)}`;

  return (
    <div
      className="mb-4"
      style={{
        padding: '0.85rem 1rem',
        background: 'linear-gradient(180deg, rgba(126, 34, 206, 0.05) 0%, rgba(126, 34, 206, 0.02) 100%)',
        border: '1.5px solid rgba(126, 34, 206, 0.35)',
        borderRadius: '0.625rem',
      }}
    >
      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
        <p
          className="font-body"
          style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7e22ce', letterSpacing: '0.08em' }}
        >
          🧪 WHAT-IF SIMULATOR
        </p>
        <p className="font-body text-xs text-ink/72" style={{ fontSize: '0.78rem' }}>
          live recalc · {totalProducts} product{totalProducts === 1 ? '' : 's'}
        </p>
      </div>

      <p className="font-body text-sm text-ink mb-2.5" style={{ lineHeight: 1.4 }}>
        Lift every product to a{' '}
        <span style={{ fontWeight: 700, color: '#7e22ce' }}>{marginFloor}%</span> margin floor
      </p>

      {/* Slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.7rem' }}>
        <span className="font-body text-xs text-ink/68" style={{ minWidth: '1.5rem' }}>15%</span>
        <input
          type="range"
          min={15}
          max={50}
          step={1}
          value={marginFloor}
          onChange={(e) => setMarginFloor(parseInt(e.target.value, 10))}
          aria-label={`Margin floor: ${marginFloor}%`}
          style={{ flex: 1, accentColor: '#7e22ce', cursor: 'pointer' }}
        />
        <span className="font-body text-xs text-ink/68" style={{ minWidth: '1.5rem', textAlign: 'right' }}>50%</span>
      </div>

      {/* Result band */}
      {affected === 0 ? (
        <div
          style={{
            padding: '0.55rem 0.7rem',
            background: 'rgba(4, 120, 87, 0.08)',
            border: '1px solid rgba(4, 120, 87, 0.4)',
            borderRadius: '0.45rem',
          }}
        >
          <p className="font-body text-sm" style={{ color: '#065f46', fontWeight: 600, margin: 0, lineHeight: 1.4 }}>
            ✓ Every product already clears {marginFloor}% margin. Try a higher floor.
          </p>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            // Wraps to 1–2 cols on narrow viewports
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '0.5rem',
          }}
        >
          <ResultStat
            value={`${affected} of ${totalProducts}`}
            label="would re-price"
            color="#7e22ce"
          />
          <ResultStat
            value={`+${fmt(lift.totalLift)}`}
            label="profit per item across catalog"
            color="#047857"
          />
          <ResultStat
            value={`+${fmtBig(lift.yearly)}/yr`}
            label="@ 30 sales/mo each"
            color="#047857"
            highlight
          />
        </div>
      )}

      {affected > 0 && (
        <details style={{ marginTop: '0.6rem' }}>
          <summary
            style={{
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8rem',
              color: '#7e22ce',
              fontWeight: 600,
              userSelect: 'none',
            }}
          >
            See the {affected} affected product{affected === 1 ? '' : 's'} →
          </summary>
          <ul
            style={{
              listStyle: 'none', padding: 0, margin: '0.45rem 0 0',
              display: 'flex', flexDirection: 'column', gap: '0.25rem',
              maxHeight: '160px', overflowY: 'auto',
            }}
          >
            {lift.affected.map(a => (
              <li
                key={a.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', padding: '0.32rem 0.55rem',
                  background: '#fefdf8',
                  border: '1px solid rgba(26,26,46,0.1)',
                  borderRadius: '0.35rem',
                  fontSize: '0.82rem',
                }}
              >
                <span className="font-body text-ink truncate" style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                  {a.name}
                </span>
                <span className="font-body text-ink/72" style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {sym}{a.oldPrice.toFixed(2)} → <span style={{ color: '#7e22ce', fontWeight: 700 }}>{sym}{a.newPrice.toFixed(2)}</span>
                </span>
                <span className="font-body" style={{ color: '#047857', fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: '3.2rem', textAlign: 'right' }}>
                  +{fmt(a.lift)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// Small stat for the simulator result band
function ResultStat({ value, label, color, highlight }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '0.45rem 0.45rem',
        background: highlight ? `${color}15` : '#fefdf8',
        border: `1px solid ${highlight ? `${color}66` : 'rgba(26,26,46,0.1)'}`,
        borderRadius: '0.45rem',
        minWidth: 0,
      }}
    >
      <p
        className="font-numbers"
        style={{
          fontSize: '1.05rem', fontWeight: 700, color,
          letterSpacing: '-0.02em', lineHeight: 1.1, margin: '0 0 0.15rem',
          fontVariantNumeric: 'tabular-nums',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {value}
      </p>
      <p
        className="font-body"
        style={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.7)', lineHeight: 1.25, margin: 0 }}
      >
        {label}
      </p>
    </div>
  );
}

// ─── KPI card sub-component ────────────────────────────────
function KpiCard({ label, value, sublabel, valueColor, accent, accentBg, icon }) {
  return (
    <div
      style={{
        padding: '0.7rem 0.85rem',
        background: accentBg,
        border: `1.5px solid ${accent}`,
        borderRadius: '0.625rem',
        minWidth: 0,
      }}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span style={{ fontSize: '1.02rem' }} aria-hidden="true">{icon}</span>
        <span
          className="font-body"
          style={{
            fontSize: '0.74rem',
            color: accent,
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          {label}
        </span>
      </div>
      <p
        className="font-numbers"
        style={{
          fontSize: '1.4rem',
          fontWeight: 700,
          color: valueColor || '#1a1a2e',
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
          fontVariantNumeric: 'tabular-nums',
          marginBottom: '0.15rem',
        }}
      >
        {value}
      </p>
      {sublabel && (
        <p
          className="font-body"
          style={{
            fontSize: '0.86rem',
            color: 'rgba(26, 26, 46, 0.78)',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sublabel}
        </p>
      )}
    </div>
  );
}
